import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AuthError,
  type Enrolment,
  assuranceLevel,
  authConfigured,
  current,
  enrolTotp,
  listFactors,
  verifyTotp,
} from '../auth/session';
import { EntryLayout } from './EntryLayout';

/**
 * B5-08b — the second factor, performed rather than explained.
 *
 * Owner and Admin are refused every request unless the session asserts `aal2`.
 * Until this screen existed the product said so and offered nothing: it told
 * people to "complete the second factor with your identity provider", which
 * nobody can do, because Supabase has no end-user interface for TOTP. The
 * first Owner account could therefore never be used, and could never invite
 * anybody who could use one.
 *
 * **Enrolling and presenting are two different things, and this screen does
 * both.** They look similar and are not: enrolling creates a factor and proves
 * once that the authenticator works; presenting is what makes *this session*
 * `aal2`. A person who enrolled last week still has to present today, because
 * the API reads assurance from the token on every request rather than from a
 * flag saying they once enrolled.
 *
 * **The secret is shown as text as well as a QR code.** A camera that will not
 * focus, a desktop authenticator, a printed backup — a QR-only screen is a
 * lockout waiting for one of those. It is shown once, on the same principle as
 * the API key it resembles.
 *
 * **The QR arrives as an image, never as markup.** It comes over the network
 * onto a page holding a session, and `<img src>` cannot execute what an
 * inlined SVG could.
 */
export function Mfa() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';

  const [phase, setPhase] = useState<'loading' | 'enrol' | 'present' | 'done'>(
    'loading',
  );
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qrBroken, setQrBroken] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const factors = await listFactors();
        const verified = factors.find((f) => f.status === 'verified');
        if (cancelled) return;
        if (verified) {
          setFactorId(verified.id);
          setPhase('present');
        } else {
          setPhase('enrol');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof AuthError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function begin() {
    setBusy(true);
    setError(null);
    try {
      const created = await enrolTotp();
      setEnrolment(created);
      setFactorId(created.factorId);
    } catch (e) {
      setError(e instanceof AuthError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotp(factorId, code);
      setPhase('done');
      // Straight on to wherever they were going. Landing back on a screen
      // about authentication after satisfying it is how somebody concludes it
      // did not work.
      navigate(next, { replace: true });
    } catch (e) {
      setError(e instanceof AuthError ? e.message : String(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (!authConfigured())
    return (
      <EntryLayout>
        <h1>Two-factor authentication</h1>
        <p role="alert">
          Sign-in is not configured in this build, so a second factor cannot be
          set up either.
        </p>
      </EntryLayout>
    );

  if (!current())
    return (
      <EntryLayout>
        <h1>Two-factor authentication</h1>
        <p role="alert">
          You are not signed in. A second factor is added to an account, so{' '}
          <Link to="/login">sign in first</Link> — the password comes before
          this, not after.
        </p>
      </EntryLayout>
    );

  const codeField = (
    <form onSubmit={submit}>
      <label htmlFor="totp">Six-digit code</label>
      <input
        id="totp"
        // `text` with a numeric mode rather than `type="number"`: a number
        // field strips leading zeros, and a code beginning 0 is one in ten.
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        required
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
      />
      {error && <p role="alert">{error}</p>}
      <button type="submit" disabled={busy || code.length !== 6}>
        {busy ? 'Checking…' : 'Verify'}
      </button>
    </form>
  );

  return (
    <EntryLayout>
      <h1>Two-factor authentication</h1>

      {phase === 'loading' && <p aria-live="polite">Loading…</p>}

      {phase === 'present' && (
        <>
          <p>
            Open your authenticator app and enter the current code for Premise
            Sense.
          </p>
          <p className="basis">
            You are enrolled already. This session still has to present the
            code, because the product checks assurance on every request rather
            than trusting that you enrolled once.
          </p>
          {codeField}
        </>
      )}

      {phase === 'enrol' && !enrolment && (
        <>
          <p>
            Owner and Admin accounts must present a second factor on every
            session. Set one up now with any authenticator app — Google
            Authenticator, 1Password, Authy or similar.
          </p>
          {error && <p role="alert">{error}</p>}
          <button type="button" onClick={begin} disabled={busy}>
            {busy ? 'Preparing…' : 'Set up two-factor authentication'}
          </button>
          <p className="basis basis--unknown">
            Do this on a device you will still have tomorrow. There is no
            recovery code yet, so if you lose the authenticator an
            administrator has to remove the factor before you can enrol a new
            one. Ask whoever administers this deployment — that is a real
            procedure, and it is the answer on the first account of an
            organization too, where there is no other administrator to ask.
          </p>
        </>
      )}

      {phase === 'enrol' && enrolment && (
        <>
          <h2>1 · Add it to your authenticator</h2>
          {/* An image, never inlined markup — and one this product drew, so a
              change in what the provider returns cannot break it. */}
          {!qrBroken && (
            <img
              className="mfa-qr"
              src={enrolment.qr}
              alt="QR code for enrolling this account in your authenticator app"
              width={200}
              height={200}
              onError={() => setQrBroken(true)}
            />
          )}
          {qrBroken && (
            // A white box with a broken image tells somebody nothing. This
            // says what happened and points at the way that still works.
            <p role="alert">
              The QR code could not be drawn. Add the account by hand with the
              key below — it is the same secret and enrols the same factor.
            </p>
          )}
          <p className="basis">
            {qrBroken
              ? 'In your authenticator, choose “Enter a setup key” and type this:'
              : 'If you cannot scan it, add the account by hand and type this key:'}
          </p>
          <p className="mfa-secret">
            <code>{enrolment.secret}</code>
          </p>
          <p className="basis basis--unknown">
            This key is shown once. It is the whole of the second factor —
            treat it as you would the password.
          </p>

          <h2>2 · Enter the code it shows</h2>
          {codeField}
        </>
      )}

      {phase === 'done' && <p aria-live="polite">Verified. Taking you back…</p>}

      <p className="basis">
        {assuranceLevel() === 'aal2'
          ? 'This session has already presented a second factor.'
          : 'This session has not presented a second factor yet.'}
      </p>
    </EntryLayout>
  );
}
